/**
 * Tracker / wave comparability API.
 * Cross-project metadata drift detection — compares the current questionnaire metadata of
 * two projects (e.g. two waves of a tracker). Read-only; nothing is recorded.
 * Authentication headers are handled automatically by the axios instance.
 */
import { AxiosInstance, AxiosResponse } from "axios"
import {
  TrackerCompareResponse,
  TrackerAddResponse,
  TrackerProjectsResponse,
} from "types/questionnaire.types"

/**
 * Compares the metadata of a base project against another project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param baseId - The base (current) project id
 * @param baseOwner - The base project's owner
 * @param otherId - The project to compare against
 * @param otherOwner - The other project's owner
 * @returns { parseStatus, identical, baseMetadataHash, targetMetadataHash, changes[] };
 *   parseStatus "no_qnr" when either project has no questionnaire
 */
export async function compareTrackerMetadata(
  axiosInstance: AxiosInstance,
  baseId: string,
  baseOwner: string,
  otherId: string,
  otherOwner: string
): Promise<TrackerCompareResponse> {
  return axiosInstance.get(
    `/tracker-metadata-compare?id=${encodeURIComponent(baseId)}&hint_owner=${encodeURIComponent(baseOwner)}&other=${encodeURIComponent(
      otherId
    )}&other_owner=${encodeURIComponent(otherOwner)}`
  )
}

/**
 * Adds an existing project to a tracker, linked to another project (a prior wave or the root).
 * The project's own history is kept: the link is recorded as "wave.linked" in its history and in
 * the root's. Refused (409) when the project is the root of that tracker, or a root with waves.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project being added
 * @param owner - The project's owner
 * @param otherId - The project to link to (its tracker is joined)
 * @param otherOwner - The other project's owner
 * @returns { trackerId, parentProjectId }
 */
export async function addToTracker(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  otherId: string,
  otherOwner: string
): Promise<TrackerAddResponse> {
  return axiosInstance.get(
    `/tracker-add?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}&other=${encodeURIComponent(
      otherId
    )}&other_owner=${encodeURIComponent(otherOwner)}`
  )
}

/**
 * Lists every project in the same tracker as the given project, root first, then the waves in
 * the order they joined. Membership is read from the histories, so it shows from any wave.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - A project in the tracker
 * @param owner - That project's owner
 * @returns { trackerId, trackerStatus, members[] }; trackerStatus says when the tracker
 *   couldn't be read (its root refused, or Repos failed)
 */
export async function getTrackerProjects(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<TrackerProjectsResponse> {
  return axiosInstance.get(
    `/tracker-projects-get?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}`
  )
}

/**
 * Removes a project from its tracker. The project's own history is kept; the other members stay
 * grouped. "wave.unlinked" is recorded in the root's history and the project's. Refused (409)
 * for a root that still has waves, or a project in no tracker.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project to remove from its tracker
 * @param owner - That project's owner
 */
export async function removeFromTracker(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<{ ok: boolean }>> {
  return axiosInstance.get(
    `/tracker-remove?id=${encodeURIComponent(id)}&hint_owner=${encodeURIComponent(owner)}`
  )
}
